/**
 * WorkspaceTemplatesSheet.js
 *
 * Per-role template picker. Tapping a template applies it as a NEW workspace in
 * one tap. Widgets of modules the user cannot access are filtered at apply time;
 * the count shown on each row is the number of widgets that will actually be placed.
 *
 * @module components/workspaces/WorkspaceTemplatesSheet
 */

import React, {useMemo} from 'react';
import {Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import PremiumSheet from '../management/PremiumSheet';
import {useManagementTheme} from '../../helpers/managementTheme';
import {resolveWidgetDefinition} from '../../config/workspaceCatalog';
import {isWidgetAllowed} from '../../helpers/workspaces/workspaceData';
import {orderTemplatesForProfile, templateKeys} from '../../helpers/workspaces/workspaceTemplates';
import {SheetRow} from './WorkspaceUI';

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {() => void} props.onClose Dismiss.
 * @param {?string} props.profile Dashboard profile (`resolveDashboardProfile`) to suggest a template for.
 * @param {?Object.<string, boolean>} props.accessMap Backend module gate (`null` = unknown).
 * @param {boolean} props.canCreate False once the workspace cap is reached.
 * @param {(template: object) => void} props.onApply Apply as a new workspace.
 * @returns {JSX.Element} The sheet.
 */
export default function WorkspaceTemplatesSheet({visible, onClose, profile, accessMap, canCreate, onApply}) {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const templates = useMemo(() => orderTemplatesForProfile(profile), [profile]);

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('wsd_templates_title')}
            subtitle={t('wsd_templates_subtitle')}
            icon="view-grid-plus-outline"
            hideFooter
        >
            {templates.map((template, index) => {
                const keys = templateKeys(template.id);
                const count = template.widgets.filter((entry) => isWidgetAllowed(resolveWidgetDefinition(entry.type), accessMap)).length;

                return (
                    <View key={template.id}>
                        {index === 0 && (
                            <Text style={{color: m.colors.primary, fontSize: 12, fontWeight: '800', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6}}>
                                {t('wsd_suggested')}
                            </Text>
                        )}
                        <SheetRow
                            icon={template.icon}
                            tone={template.tone}
                            title={t(keys.title)}
                            subtitle={`${t(keys.description)}  ·  ${t('wsd_tpl_widgets_count', {count})}`}
                            disabled={!canCreate || count === 0}
                            onPress={() => onApply(template)}
                        />
                    </View>
                );
            })}
        </PremiumSheet>
    );
}
